import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ChevronRight,
  ExternalLink,
  Layers,
  Package,
  Plus,
  Search,
  TrendingUp,
  X,
} from "lucide-react";
import { Badge, productStatusBadge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { useApp, type ProductStatus } from "@/context/AppContext";
import { useRecommendationSummary } from "@/hooks/useRecommendationSummary";

export const Route = createFileRoute("/products/")({
  component: ProductsListPage,
});

const STATUSES: (ProductStatus | "All")[] = [
  "All",
  "Draft",
  "Ready to List",
  "Picked",
  "Packed",
  "Listed",
  "Sold",
  "Shipped",
];

type ArchiveView = "active" | "archived" | "all";
const ARCHIVE_VIEWS: { value: ArchiveView; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

function ProductsListPage() {
  const navigate = useNavigate();
  const { products, liveListings } = useApp();
  const { count: recCount, highCount: recHighCount } =
    useRecommendationSummary();

  const liveByProduct = useMemo(() => {
    const map = new Map<string, { url?: string; id?: string }>();
    for (const l of liveListings) {
      if (l.ebayListingUrl || l.ebayListingId) {
        map.set(l.productId, { url: l.ebayListingUrl, id: l.ebayListingId });
      }
    }
    return map;
  }, [liveListings]);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<ProductStatus | "All">(
    "All",
  );
  const [archiveView, setArchiveView] = useState<ArchiveView>("active");
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Per-view counts so the tab labels carry a quick count badge — operator
  // can see at a glance how many archived products are waiting in the
  // background even when looking at the Active list.
  const archiveCounts = useMemo(() => {
    let archived = 0;
    for (const p of products) {
      if (p.archivedAt) archived++;
    }
    return { archived, active: products.length - archived };
  }, [products]);

  const filtered = useMemo(() => {
    let result = [...products];
    if (archiveView === "active") {
      result = result.filter((p) => !p.archivedAt);
    } else if (archiveView === "archived") {
      result = result.filter((p) => !!p.archivedAt);
    }
    if (filterStatus !== "All")
      result = result.filter((p) => p.status === filterStatus);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.productType.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q),
      );
    }
    return result.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [products, search, filterStatus, archiveView]);

  const goToCreate = () => navigate({ to: "/products/create" });
  const goToStudio = () => navigate({ to: "/products/studio" });

  const recBannerUrgent = recHighCount > 0;

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Products</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goToStudio}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              <Layers className="h-3.5 w-3.5" />
              Studio
            </button>
            <button
              type="button"
              onClick={goToCreate}
              aria-label="New product"
              className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-secondary-foreground hover:opacity-90"
            >
              <Plus className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex border-b border-border">
          {ARCHIVE_VIEWS.map((v) => {
            const active = archiveView === v.value;
            const count =
              v.value === "active"
                ? archiveCounts.active
                : v.value === "archived"
                  ? archiveCounts.archived
                  : products.length;
            return (
              <button
                key={v.value}
                type="button"
                onClick={() => setArchiveView(v.value)}
                className={`relative -mb-px border-b-2 px-3 py-1.5 text-xs font-semibold ${
                  active
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {v.label}
                {count > 0 && (
                  <span
                    className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      active
                        ? "bg-primary/15 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-1 pb-1">
          {STATUSES.map((s) => {
            const active = filterStatus === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setFilterStatus(s)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {recCount > 0 && !bannerDismissed && (
          <div
            className={`mb-3 flex items-center gap-2 overflow-hidden rounded-md border ${
              recBannerUrgent
                ? "border-destructive/55 bg-destructive/10"
                : "border-secondary/55 bg-secondary/15"
            }`}
          >
            <button
              type="button"
              onClick={() => void navigate({ to: "/tools/recommendations" })}
              className="flex flex-1 items-center gap-2 px-3 py-2.5 text-left"
            >
              {recBannerUrgent ? (
                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
              ) : (
                <TrendingUp className="h-3.5 w-3.5 text-secondary" />
              )}
              <span
                className={`flex-1 truncate text-xs font-medium ${
                  recBannerUrgent ? "text-destructive" : "text-secondary"
                }`}
              >
                {recCount} recommendation{recCount !== 1 ? "s" : ""} waiting
                {recHighCount > 0 ? ` (${recHighCount} urgent)` : ""} — tap to
                view
              </span>
              <ChevronRight
                className={`h-3.5 w-3.5 ${
                  recBannerUrgent ? "text-destructive" : "text-secondary"
                }`}
              />
            </button>
            <button
              type="button"
              onClick={() => setBannerDismissed(true)}
              aria-label="Dismiss"
              className="px-3 py-2.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Package className="h-8 w-8" />}
            title="No products yet"
            description="Build bundles and products from your inventory"
            actionLabel="Create Product"
            onAction={goToCreate}
          />
        ) : (
          <div className="space-y-2.5">
            {filtered.map((product) => {
              const roi =
                product.costBasis > 0
                  ? ((product.targetSalePrice - product.costBasis) /
                      product.costBasis) *
                    100
                  : 0;
              const ebay = liveByProduct.get(product.id);
              return (
                <div
                  key={product.id}
                  className={`rounded-md border bg-card transition-colors hover:bg-card/80 ${
                    product.archivedAt
                      ? "border-amber-300/60 opacity-60 dark:border-amber-700/40"
                      : "border-border"
                  }`}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate({
                        to: "/products/$id",
                        params: { id: product.id },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate({
                          to: "/products/$id",
                          params: { id: product.id },
                        });
                      }
                    }}
                    className="cursor-pointer p-3.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                          {product.id}
                        </div>
                        <div className="truncate text-[15px] font-semibold text-foreground">
                          {product.name || product.productType}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {product.productType} ·{" "}
                          {product.cardIds?.length ?? product.cardCount} cards
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <div className="text-lg font-bold text-foreground">
                          ${product.targetSalePrice.toFixed(2)}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Badge
                            label={product.status}
                            variant={productStatusBadge(product.status)}
                          />
                          {product.productType === "Imported" && (
                            <span
                              className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold text-sky-700"
                              title="Imported from eBay; cards not linked locally"
                            >
                              Imported
                            </span>
                          )}
                        </div>
                        {product.archivedAt && (
                          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                            Archived
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="my-2.5 h-px bg-border" />

                    <div className="flex justify-between text-xs font-medium">
                      <span className="text-muted-foreground">
                        Cost: ${product.costBasis.toFixed(2)}
                      </span>
                      <span
                        className={
                          roi >= 0 ? "text-emerald-600" : "text-destructive"
                        }
                      >
                        ROI: {roi.toFixed(0)}%
                      </span>
                      <span className="text-muted-foreground">
                        Qty: {product.quantity}
                      </span>
                    </div>

                    {ebay?.url && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            ebay.url,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline"
                      >
                        <ExternalLink className="h-2.5 w-2.5" />
                        View on eBay
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
